<template>
  <div class="legacy">
    <Loader v-if="loading" />
    <form v-else autocomplete="off">
      <div class="box">
        <template v-if="showCardDetails">
          <div v-if="!updating" class="mb-10">
            <heading class="mb-6 font-bold" :level="2">{{ $t('Your Card Details') }}</heading>
            <div class="flex justify-between">
              <div class="w-1/2 flex flex-col content-end">
                <p class="mb-4">
                  <span class="font-semibold">{{ `${$t('Brand')}: ` }}</span>
                  {{ billingDetails.card.brand }}
                </p>
                <p class="mb-4">
                  <span class="font-semibold">{{ `${$t('Number')}: ` }}</span>
                  {{ billingDetails.card.number }}</p>
                <p class="mb-4">
                  <span class="font-semibold">{{ `${$t('Expiration date')}: ` }}</span>
                  {{ billingDetails.card.expiry }}
                </p>
                <div class="flex">
                  <progress-button
                    class="bg-green mr-2"
                    @click.native="updating = true"
                  >
                    {{ $t('Update') }}
                  </progress-button>
                </div>
              </div>
            </div>
          </div>
          <div v-else>
            <stripe-card v-show="!loggedInUser()._legacy_pays_by_invoice" v-model="billingDetails.name" />
            <progress-button
              class="bg-green self-end mb-10 mr-2"
              :disabled="confirmingCardPayment || updatingPaymentMethod"
              :processing="confirmingCardPayment || updatingPaymentMethod"
              @click.native="submitPaymentMethod"
            >
              {{ $t('Save') }}
            </progress-button>
            <progress-button
              class="bg-red btn-secondary"
              @click.native="updating = false"
            >
              {{ $t('Discard changes') }}
            </progress-button>
          </div>
        </template>
        <div
          v-if="subscription.failure
                        && ['authentication_required', 'requires_action'].includes(subscription.failure.code)"
          class="mb-6 w-1/2 text-left"
        >
          <progress-button
            class="bg-green"
            :disabled="confirmingCardPayment || updatingPaymentMethod"
            :processing="confirmingCardPayment || updatingPaymentMethod"
            @click.native="confirmCardPayment(subscription.failure.payment_method)"
          >
            {{ $t('Confirm card payment') }}
          </progress-button>
        </div>
        <billing-address
          :billing-details="billingDetails"
          @input="billingDetails[$event.name] = $event.value"
        />
      </div>

      <div class="mt-4">
        <progress-button
          class="bg-green"
          :disabled="confirmingCardPayment || updatingPaymentMethod"
          :processing="confirmingCardPayment || updatingPaymentMethod"
          @click.native="save"
        >
          {{ $t('Save') }}
        </progress-button>
      </div>
    </form>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex';
  import * as stripeService from '@/_legacy/services/stripe';
  import { eventBus } from '@/_legacy/eventBus';
  import StripeCard from '@/_legacy/components/Subscriptions/StripeCard.vue';
  import billingService from '@/_legacy/services/billing';
  import BillingAddress from '@/_legacy/components/Subscriptions/BillingAddress.vue';
  import authMixin from '@/_legacy/mixins/authMixin';
  import Loader from '@/_legacy/components/Icons/Loader';
  import { fetchUser } from '@/ui/auth/auth';
  import { TrialStatus } from '@/entities/organization/constants';
  import { useTask } from '@/ui/concerns';
  import { updateCustomer } from '@/services/api/resources/subscription/actions';
  import subscriptionMixin from '@/_legacy/mixins/subscriptionMixin';
  import * as track from '@/services/telemetry';

  export default {
    name: 'BillingDetails',

    components: {
      BillingAddress,
      StripeCard,
      Loader,
    },

    mixins: [authMixin, subscriptionMixin],

    data() {
      return {
        loading: true,
        updating: false,
        confirmingCardPayment: false,
        updatingPaymentMethod: false,
        billingDetails: null,
        intentToken: {},
      };
    },

    computed: {
      ...mapGetters({
        subscription: 'subscription/subscription',
      }),
      showCardDetails() {
        return !this.loggedInUser()._legacy_pays_by_invoice
          || (
            this.loggedInUser().organization.trialStatus === TrialStatus.FREE_TRIAL_STATUS_CURRENT
            && !this.loggedInUser()._legacy_pays_by_invoice
            && this.billingDetails.card
            && this.subscription.quantity
            && this.loggedInUser().organization.subscription
            && this.loggedInUser().organization.subscription.hasPaymentMethod
          );
      }
    },

    watch: {
      subscription() {
        this.billingDetails = this.subscription.billing_details;
      },
      billingDetails(value) {
        if (value) {
          this.loading = false;
        }
      },
    },

    async created() {
      await stripeService.getStripe();
      await this.getSubscription();
    },

    async mounted() {
      this.getIntent();
      await this.getSubscription();
    },

    methods: {
      ...mapActions({
        getSubscription: 'subscription/getSubscription',
      }),
      getIntent() {
        billingService.getIntent()
          .then(setupIntent => {
            this.intentToken = setupIntent;
          });
      },

      update() {
        this.updating = false;
      },

      async save() {
        this.updatingPaymentMethod = true;
        if (this.loggedInUser()._legacy_pays_by_invoicee) {
          useTask(
            () => updateCustomer({
              ...this.billingDetails,
              name: this.loggedInUser().name,
            }),
            () => {
              this.$toasted.show(
                this.$t('Successfully updated!'),
                { type: 'success' },
              );
              this.$router.push({
                name: 'subscription',
              });
            }
          ).run();

          return;
        }
        if (this.updating) {
          this.submitPaymentMethod();
          return;
        }

        await this.updateVat();
        await billingService.updatePaymentMethod(this.billingDetails)
          .then(() => {
            this.$toasted.show(
              this.$t('Successfully updated!'),
              { type: 'success' },
            );
            this.$router.push({
              name: 'subscription',
            });
          });
      },

      confirmCardPayment(paymentMethod) {
        this.confirmingCardPayment = true;
        return stripeService.confirmCardPayment(this.subscription.failure.intent_secret, {
          payment_method: paymentMethod,
        })
          .then(res => {
            this.updatingPaymentMethod = false;
            this.confirmingCardPayment = false;
            if (res.error) {
              eventBus.$emit(
                'error',
                this.$t('Saving your card failed. Please try again {message}', {
                  message: res.error.message,
                }),
              );
            } else {
              track.purchase({
                id: res.setupIntent.id,
                total: this.total,
                currency: this.selectedPlan.currency,
                interval: this.selectedPlan.interval,
                nickname: this.selectedPlan.nickname,
                pricePerUser: this.pricePerUser,
                usersCount: this.usersCount,
              });
              this.$toasted.show(
                this.$t('Successfully updated!'),
                { type: 'success' },
              );
              this.$router.push({
                name: 'subscription',
              });

              fetchUser()();
            }
          });
      },

      confirmCardSetup(paymentMethod) {
        stripeService.confirmCardSetup(this.intentToken.client_secret, {
          payment_method: paymentMethod,
        })
          .then(res => {
            this.updatingPaymentMethod = false;
            if (res.error) {
              eventBus.$emit('error', res.error.message);
            } else {
              track.purchase({
                id: res.setupIntent.id,
                total: this.total,
                currency: this.selectedPlan.currency,
                interval: this.selectedPlan.interval,
                nickname: this.selectedPlan.nickname,
                pricePerUser: this.pricePerUser,
                usersCount: this.usersCount,
              });
              this.$toasted.show(
                this.$t('Successfully updated!'),
                { type: 'success' },
              );
              this.$router.push({
                name: 'subscription',
              });

              fetchUser()();
            }
          });
      },

      async submitPaymentMethod() {
        this.updatingPaymentMethod = true;
        const result = await stripeService.createPaymentMethod(this.billingDetails);
        if (result.error) {
          this.updatingPaymentMethod = false;
          eventBus.$emit('error', this.$t('Saving your card failed. {message}', {
            message: result.error.message,
          }));
          return;
        }
        await this.updateVat();
        await billingService.savePaymentMethod(result.paymentMethod.id, this.billingDetails.company);
        if (this.subscription.failure && this.subscription.failure.intent_secret) {
          this.confirmCardPayment(result.paymentMethod.id);
          return;
        }
        this.confirmCardSetup(result.paymentMethod.id);
      },

      async updateVat() {
        if (this.billingDetails.vat) {
          await billingService.addCustomerTaxId(this.billingDetails.vat)
            .catch(error => {
              eventBus.$emit('error', error.response.data.message);
            });
        } else {
          await billingService.removeCustomerTaxId()
            .catch(error => {
              eventBus.$emit('error', error.response.data.message);
            });
        }
      },
    },
  };
</script>
